/**
 * Global Keyboard Shortcuts Hook
 * Provides application-wide keyboard navigation
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router'

interface KeyboardShortcutOptions {
  /** Enable/disable shortcuts */
  enabled?: boolean
}

/**
 * Registers global keyboard shortcuts for the chat application.
 *
 * Shortcuts:
 * - Cmd/Ctrl+N: New chat (navigate to /chat)
 * - Cmd/Ctrl+,: Profile/Settings (navigate to /profile)
 * - Escape: Clear focus / collapse panels
 */
export function useKeyboardShortcuts(options: KeyboardShortcutOptions = {}) {
  const { enabled = true } = options
  const navigate = useNavigate()

  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey

      // Don't trigger in input fields
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        // Only allow Escape in inputs
        if (e.key !== 'Escape') return
      }

      // Cmd/Ctrl+N - New chat
      if (isMod && e.key === 'n') {
        e.preventDefault()
        navigate('/chat')
      }

      // Cmd/Ctrl+, - Profile/Settings
      if (isMod && e.key === ',') {
        e.preventDefault()
        navigate('/profile')
      }

      // Escape - clear focus / collapse panels
      if (e.key === 'Escape') {
        ;(document.activeElement as HTMLElement)?.blur?.()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enabled, navigate])
}
